<script setup>
// Gráfico visual de funil. Cada estágio é uma barra cuja largura é
// proporcional ao count relativo ao primeiro estágio. Entre as barras,
// label com a taxa de conversão.
//
// Quando não há leads no período, este componente não deve ser renderizado
// - o caller (FunnelReport.vue) mostra um empty state mais informativo.
import { computed } from 'vue';

const props = defineProps({
  stages: {
    // Array de { stage_id, stage_name, kind, count }
    type: Array,
    default: () => [],
  },
  conversion: {
    // Array opcional de { from, to, rate }
    type: Array,
    default: () => [],
  },
});

const KIND_COLOR = {
  open:      '#6366f1', // indigo (n-brand)
  qualified: '#f59e0b', // amber
  won:       '#10b981', // emerald
  lost:      '#ef4444', // red
  discarded: '#94a3b8', // slate
};

const validStages = computed(() =>
  Array.isArray(props.stages) ? props.stages : []
);

const topCount = computed(() => validStages.value[0]?.count || 0);

const conversionByPair = computed(() => {
  const map = {};
  for (const c of props.conversion || []) {
    map[`${c.from}__${c.to}`] = c.rate;
  }
  return map;
});

// Largura da barra como % do TOPO (não do max global) - caracteriza o funil.
// Mínimo 6% pra manter o stub visível e legível para estágios com 0 leads.
const MIN_WIDTH_PCT = 6;

function widthPct(count) {
  if (!topCount.value) return MIN_WIDTH_PCT;
  const pct = (count / topCount.value) * 100;
  return Math.max(MIN_WIDTH_PCT, pct);
}

function colorFor(kind) {
  return KIND_COLOR[kind] || KIND_COLOR.open;
}

function pctOfTop(stage) {
  if (!topCount.value) return '0.0';
  return ((stage.count / topCount.value) * 100).toFixed(1);
}

function conversionToNext(idx) {
  const cur = validStages.value[idx];
  const nxt = validStages.value[idx + 1];
  if (!cur || !nxt) return null;
  const key = `${cur.stage_name}__${nxt.stage_name}`;
  return conversionByPair.value[key] ?? null;
}
</script>

<template>
  <div class="w-full">
    <ol class="flex flex-col gap-2">
      <li v-for="(stage, idx) in validStages" :key="stage.stage_id" class="flex flex-col">
        <!-- Linha do estágio: trilha cinza + barra colorida proporcional + valores fora -->
        <div class="grid grid-cols-[12rem_1fr_4.5rem_3.5rem] items-center gap-3">
          <!-- Nome do estágio + bullet de cor -->
          <div class="flex items-center gap-2 min-w-0">
            <span class="size-2.5 rounded-full shrink-0" :style="{ background: colorFor(stage.kind) }" />
            <span class="text-body-main font-medium text-n-slate-12 truncate">{{ stage.stage_name }}</span>
          </div>

          <!-- Barra: trilha + preenchimento proporcional -->
          <div class="relative h-9 rounded-md bg-n-alpha-2 overflow-hidden">
            <div
              class="absolute inset-y-0 left-0 flex items-center px-3 transition-all duration-300"
              :style="{
                width: `${widthPct(stage.count)}%`,
                background: colorFor(stage.kind),
              }"
            >
              <span class="text-body-small font-semibold text-white drop-shadow whitespace-nowrap">
                {{ stage.count }}
              </span>
            </div>
          </div>

          <!-- % do topo -->
          <span class="text-body-small text-n-slate-11 text-right tabular-nums">
            {{ pctOfTop(stage) }}%
          </span>

          <!-- Tipo -->
          <span
            class="text-label-small uppercase tracking-wide font-medium text-right"
            :style="{ color: colorFor(stage.kind) }"
          >
            {{ stage.kind }}
          </span>
        </div>

        <!-- Conversão para o próximo estágio (entre as linhas) -->
        <div
          v-if="idx < validStages.length - 1"
          class="grid grid-cols-[12rem_1fr_4.5rem_3.5rem] gap-3 my-1"
        >
          <div />
          <div class="flex items-center gap-1.5 pl-3 text-label-small text-n-slate-11">
            <i class="i-lucide-arrow-down size-3" />
            <template v-if="conversionToNext(idx) != null">
              <b class="text-n-slate-12">{{ conversionToNext(idx) }}%</b> convertem para o próximo
            </template>
            <template v-else>
              -
            </template>
          </div>
        </div>
      </li>
    </ol>

    <!-- Legenda -->
    <div class="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-n-weak text-label-small text-n-slate-11">
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full" :style="{ background: KIND_COLOR.open }" /> Aberto
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full" :style="{ background: KIND_COLOR.qualified }" /> Qualificado
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full" :style="{ background: KIND_COLOR.won }" /> Ganho
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="size-2 rounded-full" :style="{ background: KIND_COLOR.lost }" /> Perdido
      </span>
    </div>
  </div>
</template>
